import React from "react";
import PropTypes from "prop-types";
import { SwitchComponent } from "@syncfusion/ej2-react-buttons";
import { TooltipComponent } from "@syncfusion/ej2-react-popups";
import { PiQuestion } from "react-icons/pi";
import classNames from "classnames";

/**
 * ToggleSwitch — Syncfusion SwitchComponent wrapper.
 *
 * Replaces the deprecated HeadlessUI-based Toggle component.
 * Renders an inline switch with optional label + description text.
 *
 * Pass `hint` to show a small **?** icon after the label that reveals a
 * tooltip on hover — replaces the need for an external InfoSection popover.
 *
 * @example
 * <ToggleSwitch
 *   label="Receive Email Notifications"
 *   description="Get updates about your account via email"
 *   hint="Includes loan status updates, document uploads, and lender communications."
 *   checked={value}
 *   onChange={onChange}
 * />
 */
export default function ToggleSwitch({
  label,
  description,
  hint,
  checked = false,
  onChange,
  disabled = false,
  className,
  ...props
}) {
  return (
    <div className={classNames("flex items-start gap-3", className)}>
      <SwitchComponent
        checked={checked}
        change={e => onChange?.(e.checked)}
        disabled={disabled}
        cssClass="e-primary"
        {...props}
      />
      {(label || description) && (
        <div className="flex flex-col">
          {label && (
            <span className="flex items-center gap-1 text-sm font-medium text-gray-900 dark:text-slate-100">
              {label}
              {hint && (
                <TooltipComponent content={hint} position="TopCenter" openDelay={100} cssClass="e-tooltip-wrap">
                  <span className="inline-flex shrink-0 cursor-help text-gray-400 transition-colors hover:text-indigo-500 dark:text-slate-500 dark:hover:text-indigo-400">
                    <PiQuestion className="size-4" aria-hidden="true" />
                  </span>
                </TooltipComponent>
              )}
            </span>
          )}
          {description && (
            <span className="text-sm text-gray-500 dark:text-gray-400">{description}</span>
          )}
        </div>
      )}
    </div>
  );
}

ToggleSwitch.propTypes = {
  /** Primary label text */
  label: PropTypes.string,
  /** Secondary description text */
  description: PropTypes.string,
  /** Tooltip hint text — renders a ? icon inline after the label */
  hint: PropTypes.string,
  /** Controlled checked state */
  checked: PropTypes.bool,
  /** Change handler — receives boolean */
  onChange: PropTypes.func,
  /** Fully disabled */
  disabled: PropTypes.bool,
  /** Additional CSS classes for the wrapper */
  className: PropTypes.string,
};
